import { Chip, Paper, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { KycCase } from '../../data/types';
import { date, daysBetween, DEMO_TODAY } from '../../lib/format';
import { KYC_TYPE_LABELS } from '../../lib/labels';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { AboutThisPage, PageHeader, StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import KycCaseDrawer from './KycCaseDrawer';

const VIEWS = [
  { key: 'open', label: 'Open', test: (item: KycCase) => item.status !== 'CLEARED' },
  {
    key: 'high',
    label: 'High severity',
    test: (item: KycCase) => item.status !== 'CLEARED' && item.severity === 'HIGH',
  },
  {
    key: 'mine',
    label: 'Assigned to compliance',
    test: (item: KycCase) => item.status !== 'CLEARED' && Boolean(item.assignedTo),
  },
  { key: 'cleared', label: 'Cleared', test: (item: KycCase) => item.status === 'CLEARED' },
];

export default function KycPage() {
  const scope = useTenantScope();
  const [view, setView] = useState('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const rows = useMemo(
    () => scope.kycCases.filter(VIEWS.find((item) => item.key === view)!.test),
    [scope.kycCases, view],
  );
  const open = scope.kycCases.filter((item) => item.status !== 'CLEARED');
  const selected = scope.kycCases.find((item) => item.id === selectedId) ?? null;
  const holders = scope.holders;

  return (
    <>
      <PageHeader
        title="KYC & AML"
        subtitle="Exceptions from identity verification, PEP and sanctions screening, and ongoing due diligence."
      />
      <AboutThisPage>
        <p>
          Most investors verify electronically during their application, so only exceptions reach
          this queue. Every holder and beneficial owner is re-screened daily against sanctions and
          PEP lists; matches open a case here. Periodic refreshes are scheduled by risk rating (3
          years low, 1 year high).
        </p>
        <p>
          Clearing a case updates the holder&apos;s KYC status and unblocks any application waiting
          on it.
        </p>
      </AboutThisPage>
      <StatGrid>
        <StatCard
          label="Open cases"
          value={open.length}
          tone={open.length ? 'warning' : 'neutral'}
        />
        <StatCard
          label="High severity"
          value={open.filter((item) => item.severity === 'HIGH').length}
          tone="danger"
        />
        <StatCard
          label="Holders verified"
          value={`${Math.round((holders.filter((holder) => holder.kycStatus === 'VERIFIED').length / Math.max(1, holders.length)) * 100)}%`}
          hint={`${holders.length} holders screened daily`}
        />
        <StatCard
          label="Oldest open case"
          value={
            open.length
              ? `${Math.max(...open.map((item) => daysBetween(item.openedAt, DEMO_TODAY)))} days`
              : '—'
          }
        />
      </StatGrid>
      <Paper variant="outlined">
        <Stack useFlexGap direction="row" spacing={1} sx={{ p: 2, flexWrap: 'wrap', rowGap: 1 }}>
          {VIEWS.map((item) => (
            <Chip
              key={item.key}
              label={`${item.label} (${scope.kycCases.filter(item.test).length})`}
              color={view === item.key ? 'primary' : 'default'}
              variant={view === item.key ? 'filled' : 'outlined'}
              onClick={() => setView(item.key)}
            />
          ))}
        </Stack>
        <DataTable
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) => setSelectedId(row.id)}
          defaultSort={{ key: 'severity', direction: 'desc' }}
          dense
          emptyMessage="Queue is clear"
          columns={[
            {
              key: 'severity',
              label: 'Severity',
              sortValue: (row) => ({ HIGH: 3, MEDIUM: 2, LOW: 1 })[row.severity],
              render: (row) => <StatusChip status={row.severity} />,
            },
            {
              key: 'type',
              label: 'Case',
              sortValue: (row) => row.type,
              render: (row) => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {KYC_TYPE_LABELS[row.type]}
                </Typography>
              ),
            },
            {
              key: 'subject',
              label: 'Subject',
              sortValue: (row) => row.subjectName,
              render: (row) => row.subjectName,
            },
            { key: 'provider', label: 'Source', hideBelow: 'md', render: (row) => row.provider },
            {
              key: 'opened',
              label: 'Opened',
              sortValue: (row) => row.openedAt,
              render: (row) => `${date(row.openedAt)} (${daysBetween(row.openedAt, DEMO_TODAY)}d)`,
            },
            {
              key: 'assigned',
              label: 'Assigned',
              hideBelow: 'md',
              render: (row) => row.assignedTo ?? '—',
            },
            { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
          ]}
        />
      </Paper>
      <KycCaseDrawer kycCase={selected} onClose={() => setSelectedId(null)} />
    </>
  );
}
